import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EMPLOYEE_FILE_KIND_LABELS, LEAVE_STATUS_LABELS, type LeavePreview, type LeaveRequest, type MyEmployee } from '@kontor/shared';
import { api, ApiError, apiUpload, queryString } from '../../api';
import { VacationFacts } from './VacationFacts';
import './hr.css';

const dateDe = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const daysText = (n: number) => `${n.toLocaleString('de-DE', { maximumFractionDigits: 1 })} ${n === 1 ? 'Arbeitstag' : 'Arbeitstage'}`;

/** Der eigene Mitarbeiter oder null, wenn der Zugang mit keinem verknüpft ist (KON-119) */
export function useMyEmployee() {
  return useQuery({ queryKey: ['me-employee'], queryFn: async () => (await api<{ data: MyEmployee | null }>('GET', '/me/employee')).data });
}

/** Lädt den eigenen Mitarbeiter und zeigt ohne Verknüpfung einen Hinweis statt des Inhalts */
function WithEmployee({ title, children }: { title: string; children: (employee: MyEmployee) => ReactNode }) {
  const mine = useMyEmployee();
  if (mine.isLoading) return null;
  if (mine.isError) return <section className="card"><h2>{title}</h2><p className="muted">Ihre Mitarbeiterdaten konnten nicht geladen werden.</p></section>;
  if (!mine.data) {
    return (
      <section className="card">
        <h2>{title}</h2>
        <p className="muted">Ihr Zugang ist mit keinem Mitarbeiter verknüpft. Personalnummer, Urlaub und Verträge erscheinen hier, sobald das Büro Ihren Zugang in der Mitarbeiterkarte verknüpft.</p>
      </section>
    );
  }
  return <>{children(mine.data)}</>;
}

/** „Mein Konto“ → Mitarbeiterdaten: Personalnummer, Stammdaten und das eigene Profilfoto (KON-119) */
export function MyEmployeeProfile() {
  return (
    <WithEmployee title="Mitarbeiterdaten">
      {(e) => (
        <section className="card">
          <h2>Mitarbeiterdaten</h2>
          <div className="me-employee">
            <div className="me-employee__main">
              <dl className="facts">
                <dt>Name</dt><dd>{e.firstName} {e.lastName}</dd>
                <dt>Personalnummer</dt><dd>{e.personnelNumber}</dd>
                {e.position && (<><dt>Position</dt><dd>{e.position}</dd></>)}
                {e.department && (<><dt>Abteilung</dt><dd>{e.department}</dd></>)}
                {e.hiredOn && (<><dt>Eintritt</dt><dd>{dateDe(e.hiredOn)}</dd></>)}
              </dl>
              <p className="muted small">Ändern kann diese Angaben das Büro.</p>
            </div>
            <MyPhoto employee={e} />
          </div>
        </section>
      )}
    </WithEmployee>
  );
}

/** „Mein Konto“ → Urlaub und Freistellung: Anspruch und Rest, darunter der Antrag (KON-120, KON-121) */
export function MyVacation() {
  return (
    <WithEmployee title="Urlaub und Freistellung">
      {(e) => (
        <div className="me-stack">
          <section className="card">
            <h2>Mein Urlaub</h2>
            <VacationFacts vacation={e.vacation} />
          </section>
          <LeaveRequestForm employee={e} />
        </div>
      )}
    </WithEmployee>
  );
}

/** „Mein Konto“ → Meine Anträge (KON-121) */
export function MyLeaveRequests() {
  return (
    <WithEmployee title="Meine Anträge">
      {(e) => (
        <section className="card">
          <h2>Meine Anträge</h2>
          {e.leaveRequests.length === 0 ? <p className="muted">Noch keine Anträge.</p> : <LeaveList requests={e.leaveRequests} />}
        </section>
      )}
    </WithEmployee>
  );
}

/** „Mein Konto“ → Dokumente zum Herunterladen (KON-119) */
export function MyFiles() {
  return (
    <WithEmployee title="Dokumente">
      {(e) => (
        <section className="card">
          <h2>Meine Dokumente</h2>
          {e.files.length === 0 ? (
            <p className="muted">Noch keine Dateien abgelegt.</p>
          ) : (
            <ul className="leave-list">
              {e.files.map((f) => (
                <li key={f.id}>
                  <span className="leave-list__what">
                    <a href={`/api/v1/me/employee/files/${f.id}/download`} className="strong-link">{f.fileName}</a>
                    <span className="muted small"> · {EMPLOYEE_FILE_KIND_LABELS[f.kind]}{f.validUntil ? ` · gültig bis ${dateDe(f.validUntil)}` : ''}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="muted small">Ändern oder ergänzen kann die Dateien das Büro.</p>
        </section>
      )}
    </WithEmployee>
  );
}

function MyPhoto({ employee }: { employee: MyEmployee }) {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    client.invalidateQueries({ queryKey: ['me-employee'] });
    setError(null);
  };
  const upload = useMutation({ mutationFn: (file: File) => apiUpload('/me/employee/photo', file), onSuccess: refresh, onError: (e) => setError(e instanceof ApiError ? e.message : 'Das Foto konnte nicht hochgeladen werden.') });
  const remove = useMutation({ mutationFn: () => api('DELETE', '/me/employee/photo'), onSuccess: refresh });
  const initials = `${employee.firstName[0] ?? ''}${employee.lastName[0] ?? ''}`.toUpperCase();
  return (
    <div className="employee-photo">
      {employee.photoVersion ? (
        <img className="employee-avatar" src={`/api/v1/me/employee/photo?v=${employee.photoVersion}`} alt="Ihr Profilfoto" style={{ width: 128, height: 128 }} />
      ) : (
        <span className="employee-avatar employee-avatar--empty" style={{ width: 128, height: 128, fontSize: 48 }} aria-hidden="true">{initials}</span>
      )}
      <div className="employee-photo__actions">
        <label className={`btn btn--secondary btn--sm${upload.isPending ? ' is-disabled' : ''}`}>
          {upload.isPending ? 'Wird hochgeladen …' : employee.photoVersion ? 'Foto ändern' : 'Foto hochladen'}
          <input
            type="file"
            accept="image/webp,image/png,image/jpeg"
            className="sr-only"
            onChange={(ev) => {
              const file = ev.target.files?.[0];
              ev.target.value = '';
              if (!file) return;
              if (file.size > 2 * 1024 * 1024) return setError('Das Foto ist größer als 2 MB.');
              upload.mutate(file);
            }}
          />
        </label>
        {employee.photoVersion && <button type="button" className="btn btn--secondary btn--sm" disabled={remove.isPending} onClick={() => window.confirm('Profilfoto entfernen?') && remove.mutate()}>Entfernen</button>}
      </div>
      <small className="muted">WebP, PNG oder JPEG, bis 2 MB</small>
      {error && <small className="field__error" role="alert">{error}</small>}
    </div>
  );
}

/** Freistellung beantragen mit Vorschau der Arbeitstage und, bei Urlaub, dem Rest danach */
export function LeaveRequestForm({ employee }: { employee: MyEmployee }) {
  const client = useQueryClient();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [kind, setKind] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    if (!kind && employee.leaveKinds[0]) setKind(employee.leaveKinds[0].value);
  }, [employee.leaveKinds, kind]);

  const ready = !!start && !!end && end >= start;
  const preview = useQuery({
    queryKey: ['leave-preview', start, end, kind],
    queryFn: async () => (await api<{ data: LeavePreview }>('GET', `/me/leave-preview${queryString({ start, end, kind })}`)).data,
    enabled: ready,
  });
  const send = useMutation({
    mutationFn: async () => (await api<{ data: LeaveRequest }>('POST', '/me/leave-requests', { start, end, kind, note: note.trim() || undefined })).data,
    onSuccess: (r) => {
      client.invalidateQueries({ queryKey: ['me-employee'] });
      setStart('');
      setEnd('');
      setNote('');
      setErrors({});
      setMessage({ ok: true, text: `Ihr Antrag ${r.inquiryReference} ist beim Büro. Den Stand sehen Sie unter „Meine Anträge“.` });
    },
    onError: (e) => {
      setErrors(e instanceof ApiError && e.status === 422 ? e.fields : {});
      setMessage({ ok: false, text: e instanceof ApiError ? (e.status === 422 ? 'Bitte prüfen Sie Ihre Eingaben.' : e.message) : 'Der Antrag konnte nicht gesendet werden.' });
    },
  });

  if (employee.leaveKinds.length === 0) {
    return (
      <section className="card">
        <h2>Freistellung beantragen</h2>
        <p className="muted">Anträge sind gerade nicht möglich, weil der Anfragetyp „Freistellung“ in der Einrichtung fehlt oder nicht passt. Bitte wenden Sie sich an das Büro.</p>
      </section>
    );
  }
  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  function submit(event: FormEvent) {
    event.preventDefault();
    setMessage(null);
    send.mutate();
  }
  const p = preview.data;

  return (
    <form className="card" onSubmit={submit} noValidate>
      <h2>Freistellung beantragen</h2>
      {message && (
        <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>
          {message.text}
          {message.ok && <> <Link to="/konto/antraege">Zu meinen Anträgen</Link></>}
        </div>
      )}
      <div className="leave-form">
        <label className="field">
          <span>Von</span>
          <input className="input" type="date" value={start} onChange={(e) => { setStart(e.target.value); if (!end || end < e.target.value) setEnd(e.target.value); }} aria-invalid={!!errors.start} />
          {err('start')}
        </label>
        <label className="field">
          <span>Bis einschließlich</span>
          <input className="input" type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} aria-invalid={!!errors.end} />
          {err('end')}
        </label>
        <label className="field">
          <span>Art</span>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value)} aria-invalid={!!errors.kind}>
            {employee.leaveKinds.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
          {err('kind')}
        </label>
        <label className="field leave-form__note">
          <span>Bemerkung (freiwillig)</span>
          <textarea className="input textarea" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
        </label>
      </div>
      {ready && p && (
        <p className="leave-preview" role="status">
          {daysText(p.workDays)}
          {p.holidays.length > 0 && <> · Feiertage im Zeitraum: {p.holidays.map((h) => `${h.name} (${dateDe(h.date)})`).join(', ')}</>}
          {p.isVacation && p.remainingAfter !== null && <> · Danach bleiben {p.remainingAfter.toLocaleString('de-DE', { maximumFractionDigits: 1 })} Urlaubstage{p.remainingAfter < 0 ? ', das reicht nicht' : ''}</>}
          {!p.isVacation && <> · Geht nicht vom Urlaub ab</>}
        </p>
      )}
      <div className="form-actions">
        <button className="btn btn--primary" type="submit" disabled={send.isPending || !ready || !kind || p?.workDays === 0}>{send.isPending ? 'Wird gesendet …' : 'Antrag senden'}</button>
      </div>
    </form>
  );
}

function LeaveList({ requests }: { requests: LeaveRequest[] }) {
  const client = useQueryClient();
  const withdraw = useMutation({ mutationFn: (id: string) => api('POST', `/me/leave-requests/${id}/withdraw`), onSuccess: () => client.invalidateQueries({ queryKey: ['me-employee'] }) });
  return (
    <ul className="leave-list">
      {requests.map((r) => (
        <li key={r.id}>
          <span className="leave-list__what">
            <strong>{r.kindLabel}</strong> {dateDe(r.start)}{r.end !== r.start ? ` bis ${dateDe(r.end)}` : ''}
            <span className="muted small"> · {daysText(r.workDays)} · {r.inquiryReference}</span>
            {r.decisionNote && <div className="muted small">Begründung: {r.decisionNote}</div>}
          </span>
          <span className={`leave-status leave-status--${r.status}`}>{LEAVE_STATUS_LABELS[r.status]}{r.decidedByName ? ` von ${r.decidedByName}` : ''}</span>
          {r.status === 'pending' && (
            <button type="button" className="btn btn--secondary btn--sm" disabled={withdraw.isPending} onClick={() => window.confirm('Antrag zurückziehen?') && withdraw.mutate(r.id)}>Zurückziehen</button>
          )}
        </li>
      ))}
    </ul>
  );
}
